import React, { useEffect, useState } from 'react';
import { DocumentFieldItem, ICountry, checkValidation, getHelperText, ValidationType } from '@adac-mp/shared';
import { Dropdown, Input, Typography, PlatesInput } from '../../index';
import { useValidatedStates } from '../../hooks/useValidatedStates';

interface IMautBoxRegistrationProps {
	inputFields?: DocumentFieldItem[];
	countries: ICountry[];
	hasError?: boolean;
	updateVehicleDataFilled?: (filled: boolean) => void;
	updateInputFields: (inputFields: DocumentFieldItem[]) => void;
}

// Key must match a documentCode from the inputFields to work
const inputFieldWarnings = {
	'V.14': 'Feld 14 wurde nicht erkannt. Bitte wählen Sie die Emissionsklasse Ihres Fahrzeuges aus.',
	E: 'Feld E wurde nicht erkannt. Bitte geben Sie die 17-stellige Fahrzeug-identifizierungsnummer an.',
	'P.3': 'Feld P.3 wurde nicht erkannt. Bitte wählen Sie die Kraftstoffart oder Energiequelle Ihres Fahrzeugs aus.',
	L: 'Feld L wurde nicht erkannt. Bitte wählen Sie die Anzahl der Achsen aus. ',
	'F.1': 'Feld F.1 wurde nicht erkannt. Bitte geben Sie die zulässige Gesamtmasse in Kilogramm an (drei- bis fünfstellig).',
	G: 'Feld G wurde nicht erkannt. Bitte geben Sie die Leermasse Ihres Fahrzeuges in Kilogramm an (drei bis fünfstellig).',
	'18': 'Feld 18 wurde nicht erkannt. Bitte geben Sie die Länge Ihres Fahrzeuges in Millimetern an (vier bis fünfstellig).',
	'19': 'Feld 19 wurde nicht erkannt. Bitte geben Sie die Breite in Millimetern an (vier- bis fünfstellig).',
	'20': 'Feld 20 wurde nicht erkannt. Bitte geben Sie die Höhe in Millimetern an (vier- bis fünfstellig).',
	'F.2': 'Feld F.2 wurde nicht erkannt. Bitte geben Sie die zulässige Gesamtmasse in Kilogramm an (drei- bis fünfstellig).',
	'O.1': 'Feld O.1 wurde nicht erkannt. Bitte geben Sie die zulässige Anhängelast in Kilogramm an. ',
};

// Key must match a documentCode from the inputFields to work
const inputFieldEmptyErrorMessages = {
	'V.14': '', // No handling for dropdowns
	E: 'Bitte geben Sie die 17-stellige Fahrzeug-identifizierungsnummer an.',
	'P.3': '', // No handling for dropdowns
	L: '', // No handling for dropdowns
	'F.1': 'Bitte geben Sie die zulässige Gesamtmasse in Kilogramm an (drei- bis fünfstellig).',
	G: 'Bitte geben Sie die Leermasse Ihres Fahrzeugs in Kilogramm an (drei bis fünfstellig).',
	'18': 'Bitte geben Sie die Länge Ihres Fahrzeuges in Millimetern an (vier bis fünfstellig).',
	'19': 'Bitte geben Sie die Breite in Millimetern an (vier- bis fünfstellig).',
	'20': 'Bitte geben Sie die Höhe in Millimetern an (vier- bis fünfstellig).',
	'F.2': 'Bitte geben Sie die zulässige Gesamtmasse in Kilogramm an (drei- bis fünfstellig).',
	'O.1': 'Bitte geben Sie die zulässige Anhängelast in Kilogramm an.',
	'D.1': 'Bitte geben Sie den Namen Ihres Fahrzeugherstellers ein.',
	'6': 'Bitte geben Sie Ihr Datum im Format TT.MM.JJ ein.',
};

// Key must match a section from the inputFields to work
const inputFieldSections = {
	1: 'Erkanntes Kennzeichen',
	2: 'Ihre Schadstoffklasse',
	3: 'Fahrzeugscheindaten',
	4: 'Fahrzeugmasse',
};

export const MautBoxRegistration = ({
	inputFields = [],
	countries,
	hasError,
	updateVehicleDataFilled,
	updateInputFields,
}: IMautBoxRegistrationProps) => {
	const [country, setCountry] = useState<ICountry | undefined>(countries[0]);

	// const getWarning = (documentValue: string | string[], dataValueRange: ValueRange[]) => {
	// 	if (dataValueRange && dataValueRange.length > 0) {
	// 		return !dataValueRange.map(({ value }) => value).includes(String(documentValue));
	// 	}
	// 	if (Array.isArray(documentValue)) {
	// 		return documentValue[0].length === 0;
	// 	}
	// 	return !documentValue;
	// };

	const getError = (documentValue, mandatory, dataType) => {
		if (!documentValue && mandatory) {
			return true;
		}
		if (!documentValue && !mandatory) {
			return false;
		}
		return dataType
			? checkValidation(documentValue, dataType.toUpperCase())
			: checkValidation(documentValue, 'STRING');
	};

	const platesHaveErrors = (documentValue: string[] | string) => {
		const currValue = typeof documentValue === 'string' ? documentValue.split('-') : documentValue;
		if (currValue.some((value) => !value)) {
			return true;
		}
		if (country) {
			return !new RegExp(country.regexlicenseplate).exec(currValue.join('')) || currValue.includes('');
		}
		return true;
	};

	const initStateValues = () => {
		const stateInit = {};
		inputFields.forEach(({ documentCode, documentValue, mandatory, dataMinLength }) => {
			const value = documentValue;
			// const warning = getWarning(value, dataValueRange);
			// const error =
			// 	documentCode === 'A'
			// 		? platesHaveErrors(value ? Array.from(value) : [''])
			// 		: getError(value, mandatory, dataType);
			// const lengthError = Array.isArray(value)
			// 	? value.join('').length < dataMinLength
			// 	: value.length < dataMinLength;

			stateInit[documentCode] = {
				value,
				mandatory,
				dataMinLength,
			};
		});
		return stateInit;
	};

	const [state, { updateWithoutError, updateLengthError, updateWithValidation, updateWithError }] =
		useValidatedStates(initStateValues());

	useEffect(() => {
		if (updateVehicleDataFilled) {
			const hasEmptyField = inputFields.some(({ documentCode, documentValue, mandatory, dataType }) => {
				const value = documentValue;
				return documentCode === 'A'
					? platesHaveErrors(value ? Array.from(value) : [''])
					: getError(value, mandatory, dataType);
			});
			updateVehicleDataFilled(!hasEmptyField);
		}

		const updatedInputFields = inputFields.map((field) => {
			const inputFieldState = state[field.documentCode];
			return { ...field, documentValue: inputFieldState.value };
		});
		updateInputFields(updatedInputFields);
	}, [state]);

	useEffect(() => {
		if (hasError) {
			inputFields.forEach(({ documentCode, documentValue, mandatory, dataType }) => {
				const value = documentValue;
				updateWithError(
					documentCode,
					value ?? '',
					documentCode === 'A'
						? platesHaveErrors(value ? Array.from(value) : [''])
						: getError(value, mandatory, dataType),
				);
			});
		}
	}, [hasError]);

	const getHelperTextVehicleData = (
		documentCode: string,
		documentData: string,
		dataMinLength?: number,
		dataType?: ValidationType,
	) => {
		if (state[documentCode].warning) {
			return inputFieldWarnings[documentCode] ?? `${documentData} wurde nicht erkannt.`;
		}

		if (state[documentCode].value.length === 0) {
			return inputFieldEmptyErrorMessages[documentCode];
		}

		if (state[documentCode].lengthError && dataMinLength) {
			return dataType === 'DATE'
				? getHelperText(dataType)
				: `Bitte geben Sie mindestens ${dataMinLength} Zeichen ein.`;
		}

		return dataType
			? getHelperText(dataType)
			: // This is an emergency fallback if the backend changes DocumentCodes without teling us
			  'Dieses Feld ist inkorrekt.';
	};

	const getSortedInputFields = () => {
		const inputsBySection: { [key: number]: DocumentFieldItem[] } = inputFields.reduce((acc, value) => {
			const { section } = value;
			acc[section] = !acc[section] ? [value] : [...acc[section], value];
			return acc;
		}, {});

		const inputsBySortOrder = Object.values(inputsBySection).map((section: DocumentFieldItem[]) =>
			section.sort((a, b) => a.sortOrder - b.sortOrder),
		);

		return inputsBySortOrder;
	};

	const renderInputElement = ({
		documentCode,
		documentData,
		dataValueRange,
		dataMaxLength,
		dataMinLength,
		dataType,
	}) => {
		if (documentCode === 'A') {
			return (
				<div key={documentCode} className="mb-2">
					<PlatesInput
						countries={countries}
						plates={state.A}
						country={country}
						setCountry={setCountry}
						onChange={({ value }) => {
							if (value) updateWithError(documentCode, value, platesHaveErrors(value));
						}}
					/>
				</div>
			);
		}

		return dataValueRange && dataValueRange.length === 0 ? (
			<Input
				key={documentCode}
				data-cy={`input: ${documentCode}`}
				placeholder={`Feld ${documentCode} | ${documentData}${documentCode === '6' ? ' (TT.MM.YY)' : ''}`}
				// 524288 is the native default value for inputs
				maxLength={dataMaxLength > 0 ? dataMaxLength : 524288}
				value={state[documentCode].value}
				hasError={state[documentCode].lengthError || state[documentCode].error}
				hasWarning={!state[documentCode].error && state[documentCode].warning}
				helperText={
					dataType
						? getHelperTextVehicleData(documentCode, documentData, dataMinLength, dataType.toUpperCase())
						: 'STRING'
				}
				onChange={(e) => {
					const value = e.target.value.trim().toUpperCase();
					const currDataType = dataType.toUpperCase();
					if (
						value.length === 0 ||
						!checkValidation(value, currDataType === 'DATE' && value ? 'DATE_FORMATTER' : currDataType)
					) {
						updateWithoutError(documentCode, value);
					}
				}}
				onBlur={(e) => {
					const value = e.target.value;
					const currDataType = dataType.toUpperCase();
					updateWithValidation(documentCode, value, () => checkValidation(value, currDataType));
					updateLengthError(documentCode, value.length < dataMinLength);
				}}
			/>
		) : (
			<Dropdown
				key={documentCode}
				data-cy={`dropdown: ${documentCode}`}
				className="w-full"
				placeholder={`Feld ${documentCode} | ${documentData} `}
				value={state[documentCode].value}
				hasError={state[documentCode].error}
				hasWarning={state[documentCode].warning}
				helperText={getHelperTextVehicleData(documentCode, documentData)}
				setValue={(value) => {
					updateWithError(documentCode, value, !value);
				}}
				items={dataValueRange ? dataValueRange.map(({ value }) => ({ value, label: value })) : []}
			/>
		);
	};

	const renderInputsBySection = () =>
		getSortedInputFields().map((section, index) => (
			<React.Fragment key={index}>
				<Typography color="title" component="h3" className="font-normal pb-3 pt-4 px-4">
					{inputFieldSections[index + 1]}
				</Typography>
				{section.map(renderInputElement)}
			</React.Fragment>
		));

	return <>{renderInputsBySection()}</>;
};
